/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, { memo } from 'react';

import { jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';

const INSIGHT_LINK_CARD_EXTENSION_TYPE = 'com.atlassian.rovo.insightLinkCard';
const INSIGHT_LINK_CARD_COMBINED_EXTENSION_KEY = 'insight:linkCard';

const isInsightLinkCardExtension = (extensionType?: string, extensionKey?: string): boolean =>
	extensionType === INSIGHT_LINK_CARD_EXTENSION_TYPE &&
	extensionKey === INSIGHT_LINK_CARD_COMBINED_EXTENSION_KEY;

const INSIGHT_LINK_CARD_GRID_EXTENSION_TYPE = 'com.atlassian.rovo.insightLinkCardGroup';
const INSIGHT_LINK_CARD_GRID_COMBINED_EXTENSION_KEY = 'insight:linkCardGroup';

const isInsightLinkCardGroupExtension = (extensionType?: string, extensionKey?: string): boolean =>
	extensionType === INSIGHT_LINK_CARD_GRID_EXTENSION_TYPE &&
	extensionKey === INSIGHT_LINK_CARD_GRID_COMBINED_EXTENSION_KEY;

const ROVO_INSIGHT_PROFILE_GROUP_EXTENSION_TYPE = 'com.atlassian.rovo.insightProfileGroup';
const ROVO_INSIGHT_PROFILE_GROUP_COMBINED_EXTENSION_KEY = 'insight:profileGroup';

const isInsightProfileGroupExtension = (extensionType?: string, extensionKey?: string): boolean =>
	extensionType === ROVO_INSIGHT_PROFILE_GROUP_EXTENSION_TYPE &&
	extensionKey === ROVO_INSIGHT_PROFILE_GROUP_COMBINED_EXTENSION_KEY;

const INSIGHT_COPY_TEXT_EXTENSION_TYPE = 'com.atlassian.rovo.insightCopyText';
const INSIGHT_COPY_TEXT_EXTENSION_KEY = 'insight:copyText';

const isCopyTextExtension = (extensionType?: string, extensionKey?: string): boolean =>
	extensionType === INSIGHT_COPY_TEXT_EXTENSION_TYPE &&
	extensionKey === INSIGHT_COPY_TEXT_EXTENSION_KEY;

export type ExtensionComponentProps = {
	content?: React.ReactNode;
	extensionType?: string;
	extensionKey?: string;
	parameters?: Record<string, unknown>;
	isFullScreen?: boolean;
};

// These await the import rather than chaining `.then()`. When a module is reachable both
// statically and dynamically within one bundle, Atlaspack internalises the dynamic import and
// returns the module namespace instead of a promise, making `.then` undefined. `await` is correct
// for both shapes.
const LazyInsightLinkCard = React.lazy(async () => {
	const module = await import(
		/* webpackChunkName: "@atlassian/rovo-platform-ui-components/insight-link-card" */ '../insight-link-card'
	);
	return { default: module.InsightLinkCard };
});

const LazyInsightLinkCardGroup = React.lazy(async () => {
	const module = await import(
		/* webpackChunkName: "@atlassian/rovo-platform-ui-components/insight-link-card-group" */ '../insight-link-card-group'
	);
	return { default: module.InsightLinkCardGroup };
});

const LazyInsightProfileGroup = React.lazy(async () => {
	const module = await import(
		/* webpackChunkName: "@atlassian/rovo-platform-ui-components/insight-profile-group" */ '../insight-profile-group'
	);
	return { default: module.InsightProfileGroup };
});

const LazyCopyText = React.lazy(async () => {
	const module = await import(
		/* webpackChunkName: "@atlassian/rovo-platform-ui-components/insight-copy-text" */ '../insight-copy-text'
	);
	return { default: module.CopyText };
});

const InsightExtensionBase = ({
	content,
	extensionType,
	extensionKey,
	parameters = {},
	isFullScreen = false,
}: ExtensionComponentProps): React.ReactNode => {
	const isPulseExperimentEnabled = FeatureGates.getExperimentValue(
		'rovo_growth_chat_pulse',
		'isEnabled',
		false,
	);

	if (
		isPulseExperimentEnabled &&
		isInsightLinkCardExtension(extensionType, extensionKey) &&
		typeof parameters?.url === 'string'
	) {
		return (
			<Box
				data-extension-type={INSIGHT_LINK_CARD_EXTENSION_TYPE}
				data-extension-key={INSIGHT_LINK_CARD_COMBINED_EXTENSION_KEY}
				data-parameters={JSON.stringify(parameters)}
			>
				<Suspense
					interactionName="conversation-assistant-content-renderer-insight-link-card-suspense"
					fallback={null}
				>
					<LazyInsightLinkCard url={parameters.url} />
				</Suspense>
			</Box>
		);
	}

	if (
		isPulseExperimentEnabled &&
		isInsightLinkCardGroupExtension(extensionType, extensionKey) &&
		Array.isArray(parameters?.urls)
	) {
		return (
			<Box
				data-extension-type={INSIGHT_LINK_CARD_GRID_EXTENSION_TYPE}
				data-extension-key={INSIGHT_LINK_CARD_GRID_COMBINED_EXTENSION_KEY}
				data-parameters={JSON.stringify(parameters)}
			>
				<Suspense
					interactionName="conversation-assistant-content-renderer-insight-link-card-group-suspense"
					fallback={null}
				>
					<LazyInsightLinkCardGroup
						urls={parameters.urls}
						variant="detail"
						isFullScreen={isFullScreen}
					/>
				</Suspense>
			</Box>
		);
	}

	if (
		isPulseExperimentEnabled &&
		isInsightProfileGroupExtension(extensionType, extensionKey) &&
		Array.isArray(parameters?.profileIds)
	) {
		return (
			<Box
				data-extension-type={ROVO_INSIGHT_PROFILE_GROUP_EXTENSION_TYPE}
				data-extension-key={ROVO_INSIGHT_PROFILE_GROUP_COMBINED_EXTENSION_KEY}
				data-parameters={JSON.stringify(parameters)}
			>
				<Suspense
					interactionName="conversation-assistant-content-renderer-insight-profile-group-suspense"
					fallback={null}
				>
					<LazyInsightProfileGroup profileIds={parameters.profileIds} isFullScreen={isFullScreen} />
				</Suspense>
			</Box>
		);
	}

	if (
		isPulseExperimentEnabled &&
		isCopyTextExtension(extensionType, extensionKey) &&
		typeof parameters?.text === 'string' &&
		parameters.text.trim().length > 0
	) {
		return (
			<Box
				data-extension-type={INSIGHT_COPY_TEXT_EXTENSION_TYPE}
				data-extension-key={INSIGHT_COPY_TEXT_EXTENSION_KEY}
				data-parameters={JSON.stringify(parameters)}
			>
				<Suspense
					interactionName="conversation-assistant-content-renderer-insight-copy-text-suspense"
					fallback={null}
				>
					<LazyCopyText text={parameters.text} />
				</Suspense>
			</Box>
		);
	}

	return content;
};

export const InsightExtension: React.MemoExoticComponent<
	({
		content,
		extensionType,
		extensionKey,
		parameters,
		isFullScreen,
	}: ExtensionComponentProps) => React.ReactNode
> = memo(InsightExtensionBase);
